/* Greatly Social – PostForm (v3.2 – UI Refresh)
 * • Same logic as v3.2, modernized UI only.
 * • Professional SaaS aesthetic (Buffer / Linear style).
----------------------------------------------------------------- */
import React, { useState, useEffect } from 'react';
import { Dropdown, Button, Spinner } from 'react-bootstrap';
import { ReactSortable } from 'react-sortablejs';
import { v4 as uuidv4 } from 'uuid';
import { Link } from 'react-router-dom';

import FilePickerModal     from './components/FilePickerModal';
import StockPickerModal    from './components/StockPickerModal';
import SchedulePickerModal from './components/SchedulePickerModal';
import ImageEditorModal    from './components/ImageEditorModal';
import VideoEditorModal    from './components/videoEditor/VideoEditorModal';
import AICaptionModal      from './components/AICaptionModal';
import AIPostCreationModal from './components/AIPostCreationModal';
import HashtagPanel        from './components/HashtagPanel';

import { validateMedia }    from '../../../utils/mediaValidator';
import { pollShotstackJob } from '../../../utils/shotstackBridge';
import { countHashtags, INSTAGRAM_LIMIT } from '../../../utils/hashtags';

const CHAR_LIMITS = {
  twitter: 280, x: 280, facebook: 63206, instagram: 2200,
  linkedin: 3000, pinterest: 500, tiktok: 2200, youtube: 5000
};

const PLATFORM_ICONS = {
  facebook: 'fab fa-facebook-f', instagram: 'fab fa-instagram',
  twitter: 'fab fa-twitter', x: 'fab fa-x-twitter',
  linkedin: 'fab fa-linkedin-in', pinterest: 'fab fa-pinterest-p',
  tiktok: 'fab fa-tiktok', youtube: 'fab fa-youtube'
};

/* ─── Inline styles (scoped, no extra CSS file needed) ─── */
const S = {
  card: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '20px 20px 16px',
    marginBottom: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: '#6b7280',
    marginBottom: 10,
    display: 'block',
  },
  mediaBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 14px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#374151',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    textDecoration: 'none',
  },
  aiBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 14px',
    borderRadius: 8,
    border: '1px solid #dbeafe',
    background: '#eff6ff',
    color: '#2563eb',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  textarea: {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 14,
    lineHeight: 1.6,
    color: '#111827',
    resize: 'vertical',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    fontFamily: 'inherit',
    minHeight: 110,
  },
  thumb: {
    width: 72,
    height: 72,
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
    position: 'relative',
    cursor: 'grab',
    flexShrink: 0,
  },
  radioCard: (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 16px',
    borderRadius: 8,
    border: `1.5px solid ${active ? '#2563eb' : '#e5e7eb'}`,
    background: active ? '#eff6ff' : '#fff',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    color: active ? '#1d4ed8' : '#374151',
    transition: 'all 0.15s ease',
    userSelect: 'none',
  }),
  submitBtn: (isNow) => ({
    padding: '10px 24px',
    borderRadius: 8,
    border: 'none',
    background: isNow ? '#2563eb' : '#7c3aed',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    transition: 'opacity 0.15s ease',
    letterSpacing: '0.01em',
  }),
  upgradeBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    borderRadius: 8,
    background: '#fefce8',
    border: '1px solid #fde68a',
    fontSize: 12.5,
    color: '#92400e',
  },
  platformBadge: (over) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 11.5,
    fontWeight: 600,
    background: over ? '#fee2e2' : '#f0fdf4',
    color: over ? '#dc2626' : '#16a34a',
    border: `1px solid ${over ? '#fca5a5' : '#bbf7d0'}`,
    marginRight: 6,
    marginBottom: 4,
  }),
};

export default function PostForm({
  caption, setCaption,
  files, setFiles,
  postType, setPostType,
  scheduleDateTime, setScheduleDateTime,
  handleCreatePost,
  instagramDrawer = null,
  selectedAccounts = [],
  userId, user,
  isFindingTime, onFindBestTime,
  hashtagSettings = null, onHashtagSettingsChange
}) {
  const [showFileModal,  setShowFileModal]  = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showSchedModal, setShowSchedModal] = useState(false);
  const [showImgEditor,  setShowImgEditor]  = useState(false);
  const [showVidEditor,  setShowVidEditor]  = useState(false);
  const [showAIModal,    setShowAIModal]    = useState(false);
  const [showAIPost,     setShowAIPost]     = useState(false);
  const [editingIdx,     setEditingIdx]     = useState(null);
  const [errorMsg,       setErrorMsg]       = useState('');
  const [renderJobs,     setRenderJobs]     = useState({});
  const [textareaFocused, setTextareaFocused] = useState(false);
  const [showHashtags,   setShowHashtags]   = useState(false);

  const charCount = caption.length;
  const tagCount  = countHashtags(caption);
  const tagsOver  = tagCount > INSTAGRAM_LIMIT;
  const limitsArr = selectedAccounts.length ? selectedAccounts.map(a => CHAR_LIMITS[a.platform] || 3000) : [280];
  const minLimit  = Math.min(...limitsArr);
  const charPct   = Math.min((charCount / minLimit) * 100, 100);
  const charOver  = charCount > minLimit;

  const guessFormat = () => selectedAccounts[0]?.options?.format || 'feed';

  const handleImageEditComplete = ({ url, file }) => {
    setFiles(prev => prev.map((f, i) => i === editingIdx ? { ...f, url, file } : f));
    setShowImgEditor(false);
  };

  const handleVideoEditComplete = ({ url, jobId }) => {
    setFiles(prev => prev.map((f, i) => i === editingIdx ? { ...f, url } : f));
    setShowVidEditor(false);
    if (jobId) setRenderJobs(prev => ({ ...prev, [url]: { id: jobId, progress: 0 } }));
  };

  const handleAISave     = newCap => setCaption(newCap);
  const handleAIPostSave = ({ imageUrl, caption: aiCaption }) => {
    setCaption(aiCaption);
    setFiles(prev => prev.length >= 10 ? prev : [...prev, { key: uuidv4(), url: imageUrl, file: null }]);
    setShowAIPost(false);
  };

  const addFiles = async arr => {
    if (!arr?.length) return;
    const format = guessFormat();
    for (const item of arr) {
      if (!item.file) continue;
      const check = await validateMedia(item.file, { format });
      if (!check.ok) { setErrorMsg(check.error); return; }
    }
    setFiles(prev => {
      const merged = [...prev];
      arr.forEach(f => { if (merged.length < 10 && !merged.some(e => e.key === f.key)) merged.push(f); });
      return merged;
    });
    setErrorMsg('');
    setShowFileModal(false);
    setShowStockModal(false);
  };

  const removeFile = idx => setFiles(prev => prev.filter((_, i) => i !== idx));

  useEffect(() => {
    Object.entries(renderJobs).filter(([, v]) => v.progress < 100).forEach(([placeholder, { id }]) => {
      pollShotstackJob(id, pct => {
        setRenderJobs(prev => ({ ...prev, [placeholder]: { ...prev[placeholder], progress: pct } }));
      })
        .then(finalUrl => {
          setFiles(prev => prev.map(f => f.url === placeholder ? { ...f, url: finalUrl } : f));
          setRenderJobs(prev => { const { [placeholder]: _, ...rest } = prev; return rest; });
        })
        .catch(console.error);
    });
  }, [renderJobs]);

  /* ─────────────────────────── UI ─────────────────────────── */
  return (
    <form onSubmit={handleCreatePost}>

      {/* ── Media & AI Card ── */}
      <div style={S.card}>
        <span style={S.sectionLabel}>Media</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>

          {/* Add Media dropdown — keep Bootstrap Dropdown for portal behaviour */}
          <Dropdown>
            <Dropdown.Toggle as="button" type="button" style={S.mediaBtn} id="media-dropdown">
              <i className="fas fa-paperclip" style={{ fontSize: 12 }} /> Add Media
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ borderRadius: 10, border: '1px solid #e5e7eb', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', padding: '4px 0', minWidth: 180 }}>
              <Dropdown.Item onClick={() => setShowFileModal(true)} type="button" style={{ fontSize: 13, padding: '8px 16px' }}>
                <i className="fas fa-upload me-2 text-muted" style={{ fontSize: 12 }} /> Upload File
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setShowStockModal(true)} type="button" style={{ fontSize: 13, padding: '8px 16px' }}>
                <i className="fas fa-images me-2 text-muted" style={{ fontSize: 12 }} /> Import Stock Content
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <button type="button" style={S.aiBtn} onClick={() => setShowAIPost(true)}>
            <span style={{ fontSize: 13 }}>✨</span> AI Post Creation
          </button>
        </div>

        {errorMsg && (
          <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: '#fee2e2', border: '1px solid #fca5a5', color: '#dc2626', fontSize: 12.5 }}>
            {errorMsg}
          </div>
        )}

        {/* Thumbnails */}
        {files.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <ReactSortable list={files} setList={setFiles} animation={150}
              style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {files.map((f, idx) => {
                const isImage = f.file?.type?.startsWith('image') || f.url.startsWith('data:image') || /\.(jpe?g|png|gif)(\?|$)/i.test(f.url);
                const job = renderJobs[f.url];
                return (
                  <div key={f.key || f.url} style={S.thumb}>
                    {isImage
                      ? <img src={f.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <video src={f.url} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    }
                    {job && (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11 }}>
                        {job.progress > 0 ? (<><div>Rendering…</div><div style={{ width: '80%', height: 3, background: '#fff3', borderRadius: 2, marginTop: 4 }}><div style={{ width: `${job.progress}%`, height: '100%', background: '#60a5fa', borderRadius: 2 }} /></div></>) : (<div className="spinner-border spinner-border-sm" />)}
                      </div>
                    )}
                    {/* Remove button */}
                    <button type="button" onClick={() => removeFile(idx)}
                      style={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(239,68,68,0.9)', color: '#fff', fontSize: 13, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                      ×
                    </button>
                    {/* Edit button */}
                    {!job && (
                      <button type="button"
                        onClick={() => { setEditingIdx(idx); isImage ? setShowImgEditor(true) : setShowVidEditor(true); }}
                        style={{ position: 'absolute', bottom: 4, left: 4, width: 18, height: 18, borderRadius: 4, border: 'none', background: 'rgba(55,65,81,0.85)', color: '#fff', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                        ✎
                      </button>
                    )}
                  </div>
                );
              })}
            </ReactSortable>
          </div>
        )}
      </div>

      {/* ── Caption Card ── */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ ...S.sectionLabel, marginBottom: 0 }}>Caption</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" aria-expanded={showHashtags} aria-controls="hashtag-panel"
              style={{ ...S.aiBtn, padding: '5px 11px', fontSize: 12, ...(showHashtags ? { border: '1px solid #2563eb', background: '#2563eb', color: '#fff', fontWeight: 600 } : {}) }}
              onClick={() => setShowHashtags(v => !v)}>
              # Hashtags
            </button>
            <button type="button" style={{ ...S.aiBtn, padding: '5px 11px', fontSize: 12 }} onClick={() => setShowAIModal(true)}>
              ✨ AI Caption
            </button>
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <textarea
            id="caption"
            style={{
              ...S.textarea,
              borderColor: textareaFocused ? '#2563eb' : '#e5e7eb',
              boxShadow: textareaFocused ? '0 0 0 3px rgba(37,99,235,0.12)' : 'none',
            }}
            rows={4}
            placeholder="What's on your mind?"
            value={caption}
            onChange={e => setCaption(e.target.value)}
            onFocus={() => setTextareaFocused(true)}
            onBlur={() => setTextareaFocused(false)}
          />

          {/* Character progress bar */}
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, height: 3, background: '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${charPct}%`, background: charOver ? '#ef4444' : charPct > 80 ? '#f59e0b' : '#22c55e', borderRadius: 2, transition: 'width 0.2s ease, background 0.2s ease' }} />
            </div>
            <span style={{ fontSize: 11.5, fontWeight: 500, color: charOver ? '#dc2626' : '#9ca3af', flexShrink: 0 }}>
              {charCount.toLocaleString('en-US')} / {minLimit.toLocaleString('en-US')}
            </span>
          </div>

          {/* Hashtag counter (Instagram allows 30 per post) */}
          <div data-testid="hashtag-counter" style={{ marginTop: 4, fontSize: 12, color: tagsOver ? '#dc2626' : '#6b7280', fontWeight: tagsOver ? 600 : 400 }}>
            {tagCount} of {INSTAGRAM_LIMIT} hashtags (Instagram's limit){tagsOver ? ` — remove ${tagCount - INSTAGRAM_LIMIT} for Instagram` : ''}
          </div>
        </div>

        {/* Per-platform char badges */}
        {selectedAccounts.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap' }}>
            {selectedAccounts.map(acc => {
              const lim  = CHAR_LIMITS[acc.platform] || 3000;
              const over = charCount > lim;
              const icon = PLATFORM_ICONS[acc.platform] || 'fas fa-hashtag';
              return (
                <span key={acc.id} style={S.platformBadge(over)}>
                  <i className={icon} style={{ fontSize: 10 }} />
                  {charCount}/{lim}
                </span>
              );
            })}
          </div>
        )}

        {/* Branding notice (free & starter plans) */}
        {user && (!user.plan || user.plan === 'free' || user.plan === 'starter') && (
          <div style={{ ...S.upgradeBar, marginTop: 12 }}>
            <i className="fas fa-tag" style={{ fontSize: 11 }} />
            <span>"Posted with Greatly Social" will be added to your post.</span>
            <Link to="/dashboard/plans" style={{ marginLeft: 4, fontWeight: 600, color: '#92400e', textDecoration: 'none' }}>
              Upgrade to remove →
            </Link>
          </div>
        )}
      </div>

      {/* ── # Hashtags panel (saved sets + AI suggestions) ── */}
      {showHashtags && (
        <div id="hashtag-panel">
          <HashtagPanel
            caption={caption} setCaption={setCaption}
            files={files} selectedAccounts={selectedAccounts}
            settings={hashtagSettings} onSettingsChange={onHashtagSettingsChange}
          />
        </div>
      )}

      {/* ── Platform-specific drawers ── */}
      {instagramDrawer && (
        <div style={{ ...S.card, padding: '14px 20px' }}>
          {instagramDrawer}
        </div>
      )}

      {/* ── Publish Settings Card ── */}
      <div style={S.card}>
        <span style={S.sectionLabel}>Publish</span>

        {/* Post Now / Schedule toggle */}
        <div style={{ display: 'flex', gap: 8, marginBottom: postType === 'schedule' ? 16 : 0 }}>
          {['now', 'schedule'].map(val => (
            <label key={val} style={S.radioCard(postType === val)}>
              <input type="radio" value={val} checked={postType === val}
                onChange={e => setPostType(e.target.value)}
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
              />
              {val === 'now'
                ? <><i className="fas fa-bolt" style={{ fontSize: 11 }} /> Post Now</>
                : <><i className="far fa-calendar" style={{ fontSize: 11 }} /> Schedule</>
              }
            </label>
          ))}
        </div>

        {/* Schedule datetime row */}
        {postType === 'schedule' && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="datetime-local"
              value={scheduleDateTime}
              onChange={e => setScheduleDateTime(e.target.value)}
              required
              style={{
                flex: 1, minWidth: 180, padding: '8px 12px', borderRadius: 8,
                border: '1px solid #e5e7eb', fontSize: 13, color: '#111827',
                outline: 'none', fontFamily: 'inherit',
              }}
            />
            <button type="button" onClick={() => setShowSchedModal(true)}
              style={{ ...S.mediaBtn, padding: '8px 13px', flexShrink: 0 }}>
              <i className="far fa-calendar-alt" style={{ fontSize: 13 }} />
            </button>
            <button type="button" onClick={onFindBestTime} disabled={isFindingTime}
              style={{ ...S.aiBtn, flexShrink: 0, opacity: isFindingTime ? 0.7 : 1 }}>
              {isFindingTime
                ? <><Spinner animation="border" size="sm" /> Finding…</>
                : <><span>✨</span> AI Best Time</>
              }
            </button>
          </div>
        )}
      </div>

      {/* ── Submit ── */}
      <button type="submit" style={S.submitBtn(postType === 'now')}>
        {postType === 'now'
          ? <><i className="fas fa-paper-plane" style={{ fontSize: 13 }} /> Publish Now</>
          : <><i className="far fa-clock" style={{ fontSize: 13 }} /> Schedule Post</>
        }
      </button>

      {/* ── Modals (unchanged) ── */}
      <FilePickerModal    show={showFileModal}  onClose={() => setShowFileModal(false)}  onFilesSelect={addFiles} />
      <StockPickerModal   show={showStockModal} onClose={() => setShowStockModal(false)} onFilesSelect={addFiles} userId={userId} />
      <SchedulePickerModal show={showSchedModal} onHide={() => setShowSchedModal(false)} onDateSelect={d => setScheduleDateTime(new Date(d).toISOString().slice(0, 16))} />
      <ImageEditorModal   show={showImgEditor}  src={editingIdx !== null ? files[editingIdx]?.url : ''} onClose={() => setShowImgEditor(false)} onComplete={handleImageEditComplete} />
      <VideoEditorModal   show={showVidEditor}  src={editingIdx !== null ? files[editingIdx]?.url : ''} onClose={() => setShowVidEditor(false)} onComplete={handleVideoEditComplete} userId={userId} />
      <AICaptionModal     show={showAIModal}    onClose={() => setShowAIModal(false)} onSave={handleAISave} />
      <AIPostCreationModal show={showAIPost}    onHide={() => setShowAIPost(false)}   onSave={handleAIPostSave} />
    </form>
  );
}